import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Modal, ModalFooter } from "../pages/Contacts";

export const BatchAllDialog = ({ onClose, onSaved }) => {
  const [plan, setPlan] = useState(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.batchAllContacts({ dry_run: true }).then(setPlan).catch(e => setError(e?.response?.data?.detail || "Could not compute the plan"));
  }, []);

  const build = async () => {
    setBuilding(true);
    try {
      const r = await api.batchAllContacts({ rebuild: plan?.existing_folder || false });
      toast.success(`Built ${r.created} batches in the "${r.folder}" folder`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
      setBuilding(false);
    }
  };

  return (
    <Modal title='Batch all contacts' subtitle='Splits your sendable base into warm-up batches, smallest first — the risky share ramps up gradually, so no batch is ever purely risky.' onClose={onClose} wide>
      {error && <p className="text-sm text-rose-600" data-testid="batch-all-error">{error}</p>}
      {!plan && !error && <p className="text-sm text-zinc-500 py-4">Computing the plan from your live contact base…</p>}
      {plan && (
        <>
          <p className="text-xs text-zinc-600 mb-3" data-testid="batch-all-summary">
            Sendable pool: <strong>{plan.pool.toLocaleString()}</strong> active contacts
            ({plan.deliverable.toLocaleString()} deliverable · {plan.risky.toLocaleString()} risky/unverified;
            undeliverable &amp; suppressed excluded). Most-engaged deliverable contacts fill the earliest batches.
          </p>
          <div className="max-h-72 overflow-y-auto rounded-md border border-zinc-200 mb-3">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium">Batch</th>
                  <th className="text-right px-3 py-1.5 font-medium">Contacts</th>
                  <th className="text-right px-3 py-1.5 font-medium">Deliverable</th>
                  <th className="text-right px-3 py-1.5 font-medium">Risky</th>
                  <th className="text-right px-3 py-1.5 font-medium">Est. bounce</th>
                </tr>
              </thead>
              <tbody>
                {plan.batches.map((b, i) => (
                  <tr key={i} className="border-t border-zinc-100" data-testid={`batch-plan-row-${i}`}>
                    <td className="px-3 py-1.5">{b.name}</td>
                    <td className="px-3 py-1.5 text-right">{b.total.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right">{b.deliverable.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right">{b.risky.toLocaleString()}</td>
                    <td className={`px-3 py-1.5 text-right ${b.est_bounce_pct >= 4 ? "text-rose-600 font-medium" : "text-emerald-700"}`}>{b.est_bounce_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-zinc-500 mb-1">
            Reputation tips: leave 2–4 days between batches, and only move to the next size if the previous send stayed
            under 4% bounce and 0.1% complaints (see Sender Health). Later batches carry a bigger risky share — watch
            the est. bounce column and slow down as it climbs.
          </p>
          {plan.existing_folder && (
            <p className="text-[11px] text-amber-700 mb-1">An "All contacts" folder already exists — building will <strong>replace</strong> its batch lists with these.</p>
          )}
        </>
      )}
      <ModalFooter onClose={onClose}>
        <button data-testid="batch-all-build" disabled={!plan || building} onClick={build} className="modal-btn-primary">
          {building ? "Building…" : plan?.existing_folder ? `Rebuild ${plan?.batches?.length || 0} batches` : `Build ${plan?.batches?.length || 0} batches`}
        </button>
      </ModalFooter>
    </Modal>
  );
};
